/**
 * Device-bound key for biometric login. The backend runs a custom ECDSA challenge-response (not
 * WebAuthn): the device registers a P-256 public key and later signs server challenges with the
 * private key, which never leaves IndexedDB (stored as a non-extractable CryptoKey).
 */

export interface DeviceRecord {
  deviceId: string
  privateKey: CryptoKey
  /** Base64 SPKI of the public key, as registered with the backend. */
  publicKeySpki: string
  /** WebAuthn credential used only as a local user-verification gate (see `biometrics.ts`). */
  webauthnCredentialId?: ArrayBuffer
}

const DB_NAME = 'lm-biometric'
const STORE = 'device'
const RECORD_KEY = 'current'
const P1363_LENGTH = 64

const toBase64 = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes))
const fromBase64 = (value: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(value), (c) => c.charCodeAt(0))

/** 128-bit random hex, like mobile `biometric_setup_screen.dart` `_generateDeviceId`. */
export function generateDeviceId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function generateDeviceKey(): Promise<{ privateKey: CryptoKey; publicKeySpki: string }> {
  const { privateKey, publicKey } = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign'])
  const spki = new Uint8Array(await crypto.subtle.exportKey('spki', publicKey))
  return { privateKey, publicKeySpki: toBase64(spki) }
}

/** One unsigned DER INTEGER: strip leading zeros, then prepend 0x00 if the high bit is set. */
function derInteger(bytes: Uint8Array): number[] {
  let start = 0
  while (start < bytes.length - 1 && bytes[start] === 0) start++
  const body = Array.from(bytes.slice(start))
  if (body[0] & 0x80) body.unshift(0)
  return [0x02, body.length, ...body]
}

/** Converts a WebCrypto IEEE-P1363 signature (`r||s`) to DER `SEQUENCE { INTEGER r, INTEGER s }`. */
export function derFromP1363(signature: Uint8Array): Uint8Array {
  if (signature.length !== P1363_LENGTH) {
    throw new Error(`Expected a ${P1363_LENGTH}-byte ECDSA signature, got ${signature.length}`)
  }
  const half = P1363_LENGTH / 2
  const body = [...derInteger(signature.slice(0, half)), ...derInteger(signature.slice(half))]
  return Uint8Array.from([0x30, body.length, ...body])
}

/** Signs the raw bytes of the base64 challenge (ECDSA/SHA-256) and returns the DER signature in base64. */
export async function signChallenge(privateKey: CryptoKey, challengeBase64: string): Promise<string> {
  const raw = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privateKey, fromBase64(challengeBase64))
  return toBase64(derFromP1363(new Uint8Array(raw)))
}

// --- IndexedDB (thin promise wrapper) ---------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb()
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE, mode).objectStore(STORE))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

export async function saveDevice(record: DeviceRecord): Promise<void> {
  await withStore('readwrite', (store) => store.put(record, RECORD_KEY))
}

export async function loadDevice(): Promise<DeviceRecord | null> {
  const record = await withStore<DeviceRecord | undefined>('readonly', (store) => store.get(RECORD_KEY))
  return record ?? null
}

export async function clearDevice(): Promise<void> {
  await withStore('readwrite', (store) => store.delete(RECORD_KEY))
}
